import React, { useContext, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Gift, Users, Send, Clock, Check } from 'lucide-react';
import { ArcaContext } from '../../context/ArcaContext.context';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { useToast } from '../ui/use-toast';
import invitePartnerWithBonification from '../../api/companies/invitePartnerWithBonification';

export interface EmployeeInviteModalV2Props {
	isOpen: boolean;
	onClose: () => void;
	companyName?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmployeeInviteModalV2: React.FC<EmployeeInviteModalV2Props> = ({
	isOpen,
	onClose,
	companyName
}) => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- userId needed for TODO: copiar enlace
	const { companyId, userId, token } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { toast } = useToast();

	const displayCompanyName = companyName || translate('employeeInvite.defaultCompanyName');

	const [email, setEmail] = useState('');
	const [sending, setSending] = useState(false);
	const [sent, setSent] = useState(false);

	const isValidEmail = EMAIL_REGEX.test(email);

	const handleSendInvite = async () => {
		if (!isValidEmail || !token || !companyId) return;

		setSending(true);
		try {
			const res = await invitePartnerWithBonification(token, companyId, email);
			if (res.success) {
				toast({
					title: translate('employeeInvite.toast.successTitle'),
					description: translate('employeeInvite.toast.successDescription').replace(
						'{email}',
						email
					)
				});
				setSent(true);
				setEmail('');
				setTimeout(() => setSent(false), 2000);
			} else {
				toast({
					variant: 'destructive',
					title: translate('employeeInvite.toast.errorTitle'),
					description: res.message || translate('employeeInvite.toast.errorDescription')
				});
			}
		} catch {
			toast({
				variant: 'destructive',
				title: translate('employeeInvite.toast.errorTitle'),
				description: translate('employeeInvite.toast.errorRetry')
			});
		} finally {
			setSending(false);
		}
	};

	const handleClose = () => {
		setEmail('');
		setSent(false);
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-lg">
						<Users className="w-5 h-5 text-secondary" />
						{translate('employeeInvite.modal.title')}
					</DialogTitle>
					<DialogDescription className="sr-only">
						{translate('employeeInvite.modal.description')}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{/* Description */}
					<p className="text-sm text-muted-foreground">
						{translate('employeeInvite.modal.benefitText')}
					</p>

					{/* Benefit cards */}
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-start gap-2 p-3 rounded-lg border bg-secondary/5 border-secondary/20">
							<Gift className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
							<div>
								<p className="text-sm font-medium">{translate('employeeInvite.modal.forYou')}</p>
								<p className="text-xs text-muted-foreground">
									{translate('employeeInvite.modal.forYouBenefit')}
								</p>
							</div>
						</div>
						<div className="flex items-start gap-2 p-3 rounded-lg border bg-primary/5 border-primary/20">
							<Users className="w-4 h-4 text-primary mt-0.5 shrink-0" />
							<div>
								<p className="text-sm font-medium">
									{translate('employeeInvite.modal.forColleague')}
								</p>
								<p className="text-xs text-muted-foreground">
									{translate('employeeInvite.modal.forColleagueBenefit')}
								</p>
							</div>
						</div>
					</div>

					{/* TODO: Recuperar "copiar enlace" como opción secundaria
					<div className="flex gap-2">
						<Input
							value={`${origenPublico()}/registro?empresa=${companyId}&ref=${userId}`}
							readOnly
							className="text-sm bg-muted/50"
							onClick={(e) => (e.target as HTMLInputElement).select()}
						/>
						<Button variant="outline" size="icon" onClick={handleCopy} className="shrink-0">
							{copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
						</Button>
					</div>
					*/}

					{/* Email input */}
					<div className="flex gap-2">
						<Input
							type="email"
							placeholder={translate('employeeInvite.modal.emailPlaceholder')}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="text-sm"
							disabled={sending}
						/>
					</div>

					{/* Send button */}
					<Button
						onClick={handleSendInvite}
						disabled={!isValidEmail || sending}
						className="w-full bg-secondary hover:bg-secondary/90 text-white gap-2"
					>
						{sent ? <Check className="w-4 h-4 text-green-300" /> : <Send className="w-4 h-4" />}
						{sending
							? translate('employeeInvite.modal.sending')
							: sent
								? translate('employeeInvite.modal.sent')
								: translate('employeeInvite.modal.sendButton')}
					</Button>

					{/* Footer */}
					<p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
						<Clock className="w-3 h-3" />
						{translate('employeeInvite.modal.footer')}
					</p>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default EmployeeInviteModalV2;
